/* eslint-disable react-refresh/only-export-components */

import * as React from 'react';
import { Slot } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 text-sm font-bold whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'rounded-xl bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active',
        secondary:
          'rounded-xl bg-secondary text-text-primary hover:bg-secondary/80 active:bg-secondary/70',
        destructive:
          'rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90',
        'destructive-text':
          'rounded-xl text-error-text hover:bg-error-surface hover:text-error-text',
        ghost:
          'rounded-xl text-text-primary hover:bg-accent active:bg-accent/80',
        'ghost-icon':
          'rounded-xl text-text-secondary hover:bg-accent hover:text-text-primary active:bg-accent/80',
        outline:
          'rounded-xl border border-border-strong bg-surface text-text-primary hover:border-border-brand hover:bg-brand-50',
        link: 'text-brand-700 underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-12 px-6 text-base',
        icon: 'size-10',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot.Root : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
